import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface SidebarSection {
  title: string;
  slug: string;
  items: {
    title: string;
    slug: string;
    href: string;
  }[];
}

interface DocsSidebarNavProps {
  sections: SidebarSection[];
  className?: string;
  onItemClick?: () => void;
}

export function DocsSidebarNav({ sections, className, onItemClick }: DocsSidebarNavProps) {
  const location = useLocation();
  const [openSections, setOpenSections] = useState<string[]>(
    sections.map((s) => s.slug)
  );
  
  const toggleSection = (slug: string) => {
    setOpenSections((prev) =>
      prev.includes(slug)
        ? prev.filter((s) => s !== slug)
        : [...prev, slug]
    );
  };
  
  return (
    <aside className={cn('w-64 shrink-0', className)}>
      <ScrollArea className="h-[calc(100vh-8rem)] pr-4">
        <nav className="space-y-2">
          {sections.map((section) => (
            <Collapsible
              key={section.slug}
              open={openSections.includes(section.slug)}
              onOpenChange={() => toggleSection(section.slug)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted rounded-md transition-colors">
                <span>{section.title}</span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    openSections.includes(section.slug) && 'rotate-180'
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-2 border-l pl-2 mt-1 space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.slug}
                        to={item.href}
                        onClick={onItemClick}
                        className={cn(
                          'block px-3 py-1.5 text-sm rounded-md transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                      >
                        {item.title}
                      </Link>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
}
